import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Grid,
  Divider
} from '@mui/material';

import FireRankBar from './FireRankBar';
import RiskRatings from './RiskRatings';


// Main component that accepts data as a prop
export default function SummaryFireRisk({ rawData, locationData }) {
  const [processedData, setProcessedData] = useState(null);
  const [error, setError] = useState(null);

  // The sentences for risk assessment, moved to be accessible by the function.
  const riskSentences = {
    // Urban status
    urban: {
      true: 'This area is within a defined urban zone,',
      false: 'This is a non-urban area,'
    },
    // Fuel risk contribution
    fuel: {
      '-2': 'and has significantly less fire-prone vegetation, greatly reducing the risk of fire spreading.',
      '-1': 'and has lower fuel hazards, which helps to mitigate fire risk.',
      '0': 'and has similar vegetation hazards to Pacific Palisades, presenting a comparable risk of fire spread.',
      '1': 'and has a high concentration of hazardous vegetation, making it more susceptible to a fast-moving fire.',
      '2': 'and has an extreme abundance of fire-prone vegetation, creating a very high risk of homes being lost.'
    },
    // Fire risk contribution
    fire: {
      '-2': 'Historical data and weather conditions indicate a much lower overall fire risk.',
      '-1': 'Historical data and weather conditions point to a lower overall fire risk.',
      '0': 'Historical data and weather conditions show a fire risk comparable to Pacific Palisades.',
      '1': 'Historical data and weather conditions suggest a higher overall fire risk.',
      '2': 'Historical data and weather conditions indicate an extreme overall fire risk.'
    },
    // Special mitigation or exacerbation phrases for combined risk
    mitigation: {
      'high_fuel_low_fire': 'However, a lower overall fire risk due to historical weather and climate patterns helps to mitigate the danger to homes.',
      'low_fuel_high_fire': 'However, a higher overall fire risk due to historical weather and climate patterns suggests that homes may still be at risk, despite the lower fuel load.',
    },
    // Summary conclusions
    conclusion: {
      low: 'Overall, the risk to homes in this area is considered low.',
      moderate: 'The combined risks place this area at a moderate risk of homes being lost.',
      same: 'The combined risks place this area at the same risk of Pacific Palisades.',
      high: 'The combination of factors places this area at a high risk of homes being lost.',
      very_high: 'The combined risk factors indicate a very high potential for homes to be lost in this area.'
    }
  };

  const riskSectionSentences = {
    fire_rank: {
      '-2': 'Moderate',
      '-1': 'High',
      '0': 'Very High',
      '1': 'Extreme',
      '2': 'Maximum'
    },
    fire_summary: {
      '-2': 'Historical data and weather conditions indicate a much lower overall fire risk than Pacific Palisades.',
      '-1': 'Historical data and weather conditions point to a lower overall fire risk than Pacific Palisades.',
      '0': 'Historical data and weather conditions show a fire risk comparable to Pacific Palisades.',
      '1': 'Historical data and weather conditions suggest a higher overall fire risk than Pacific Palisades.',
      '2': 'Historical data and weather conditions indicate an extreme overall fire risk than Pacific Palisades.'
    },
    fuel_rank: {
      '-2': 'Moderate',
      '-1': 'High',
      '0': 'Very High',
      '1': 'Extreme',
      '2': 'Maximum'
    },
    fuel_summary: {
      '-2': 'This area has significantly less fire-prone vegetation, greatly reducing the risk of fire spreading.',
      '-1': 'This area has lower fuel hazards, which helps to mitigate fire risk.',
      '0': 'This area has similar vegetation hazards to Pacific Palisades, presenting a comparable risk of fire spread.',
      '1': 'This area has a high concentration of hazardous vegetation, making it more susceptible to a fast-moving fire.',
      '2': 'This area has an extreme abundance of fire-prone vegetation, creating a very high risk of homes being lost.'
    },
  };

  const getFuelRiskDetails = (data) => {
    const riskGroups = {
      5: { label: 'Very High Risk', items: [] },
      4: { label: 'High Risk', items: [] },
      3: { label: 'Moderate Risk', items: [] },
      2: { label: 'Low Risk', items: [] },
      1: { label: 'No Risk', items: [] },
    };

    // Filter out 'URBAN' and group data by 'value'
    data
      .filter((item) => item.category !== 'URBAN')
      .forEach((item) => {
        if (riskGroups[item.value]) {
          riskGroups[item.value].items.push(item);
        }
      });

    const sortedValues = [5, 4, 3, 2, 1];
    return (
      <Box>
        {sortedValues.map((value) => {
          const group = riskGroups[value];
          if (group.items.length > 0) {
            const formattedItems = group.items
              .map((item) => {
                if (item.category === 'BARREN/OTHER') {
                  return item.name;
                }
                const formattedCategory = item.category.charAt(0).toUpperCase() + item.category.slice(1).toLowerCase();
                return `${item.name} (${formattedCategory})`;
              })
              .join(', ');

            return (
              <Box key={value} sx={{ mb: 0 }}>
                <Typography variant="body1">
                  <strong>{group.label}:</strong> {formattedItems}
                </Typography>
              </Box>
            );
          }
          return null;
        })}
      </Box>
    );
  };

  /**
   * Processes the raw data object to generate headline, summary, description, and total score.
   * This function combines the logic from the original Node.js script's `generateContent` and `processFile`.
   * @param {object} data - The parsed JSON data for a specific location.
   * @returns {{...data, headline: string, summary: string, description: string, totalScore: number}} The processed data object.
   */
  const processData = (data) => {
    // Check for valid data structure
    if (!data || !data.fuel_risk || typeof data.fuel_risk.score !== 'number' ||
      !data.fire_risk || typeof data.fire_risk.score !== 'number') {
      throw new Error('Invalid data structure provided.');
    }

    const fuelScore = String(data.fuel_risk.score);
    const fireScore = String(data.fire_risk.score);
    const isUrban = data.urban;

    let title = '';
    let headline = '';
    let summary = '';
    let conclusion = '';

    // Add the fixed description
    const description = `In comparison to Pacific Palisades, which is a Very High Fire Hazard Severity Zone, this report highlights how local vegetation and long-term weather trends influence the fire danger profile of this location.`;

    const urbanSentence = riskSentences.urban[isUrban];
    const fuelSentence = riskSentences.fuel[fuelScore];
    const fireSentence = riskSentences.fire[fireScore];

    // Determine headline and conclusion based on the combined risk
    var intFuelScore = parseInt(fuelScore);
    var intFireScore = parseInt(fireScore);
    var totalScore = intFuelScore + intFireScore;

    if (intFuelScore > 0 && intFireScore == -1) {
      totalScore = 0;
    }

    if (totalScore <= -2) {
      title = 'Moderate Fire Risk Zone';
      headline = 'Significantly Safer Area Compared to Pacific Palisades';
      conclusion = riskSentences.conclusion.low;
    } else if (totalScore < 0) {
      title = 'High Fire Risk Zone';
      headline = 'Lower Risk Compared to Pacific Palisades';
      conclusion = riskSentences.conclusion.moderate;
    } else if (totalScore === 0) {
      title = 'Very High Fire Risk Zone';
      headline = 'Similar Risk Compared to Pacific Palisades';
      conclusion = riskSentences.conclusion.same;
    } else if (totalScore <= 2) {
      title = 'Extreme Fire Risk Zone';
      headline = 'Higher Risk Compared to Pacific Palisades';
      conclusion = riskSentences.conclusion.high;
    } else {
      title = 'Maximum Fire Risk Zone';
      headline = 'Significantly Higher Risk Compared to Pacific Palisades';
      conclusion = riskSentences.conclusion.very_high;
    }

    // Build the final summary paragraph
    summary += `${urbanSentence} ${fuelSentence} ${fireSentence}`;

    // Apply the new logic for interacting risks
    if ((fuelScore === '1' || fuelScore === '2') && (fireScore === '-1' || fireScore === '-2')) {
      summary += ` ${riskSentences.mitigation.high_fuel_low_fire}`;
      conclusion = 'This area is at a lower risk for homes because the low fire risk mitigates the high fuel risk.';
    } else if ((fuelScore === '-1' || fuelScore === '-2') && (fireScore === '1' || fireScore === '2')) {
      summary += ` ${riskSentences.mitigation.low_fuel_high_fire}`;
      conclusion = 'This area is at a higher risk for homes because the high fire risk exacerbates the low fuel risk.';
    }

    // Add the final conclusion
    summary += ` ${conclusion}`;

    // Return the new data object with the added properties
    return {
      ...data,
      title,
      headline,
      summary,
      description,
      totalScore
    };
  };


  const calculateFireRiskValue = (data, score) => {

    var percent = (data.order / data.total) * 20;

    switch (score) {
      case -2:
        percent += 0;
        break;
      case -1:
        percent += 20;
        break;
      case 0:
        percent += 40;
        break;
      case 1:
        percent += 60;
        break;
      case 2:
        percent += 80;
        break;
    }

    return percent;
  };

  // Use a useEffect hook to process the data whenever the prop changes.
  useEffect(() => {
    if (rawData) {
      try {
        const result = processData(rawData);
        setProcessedData(result);
        setError(null);
      } catch (e) {
        setError(e.message);
        setProcessedData(null);
      }
    } else {
      setProcessedData(null);
      setError(null);
    }
  }, [rawData]);

  const reportData = rawData;

  // Helper function to get the color based on score
  const getScoreColor = (score) => {
    switch (score) {
      case -2: return '#fcd34d'; // yellow-300
      case -1: return '#eab308'; // yellow-500
      case 0: return '#f97316'; // orange-500
      case 1: return '#dc2626'; // red-600
      case 2: return '#7f1d1d'; // red-900
      default: return '#cbd5e1'; // gray-300
    }
  };

  // Helper function to get the display text based on score
  const getScoreDisplay = (score) => {
    switch (score) {
      case -2: return 'Lower than Pacific Palisades';
      case -1: return 'Slightly Lower than Pacific Palisades';
      case 0: return 'Same as Pacific Palisades';
      case 1: return 'Slightly Higher than Pacific Palisades';
      case 2: return 'Higher than Pacific Palisades';
      default: return 'Unknown';
    }
  };

  const ScoreBar = ({ label, score, isLarge = false }) => {
    const minScore = -2,
      maxScore = 2;
    const percent = ((score - minScore) / (maxScore - minScore)) * 100;

    return (
      <Box sx={{ mb: isLarge ? 3 : 2 }}>
        <Typography variant={isLarge ? "h6" : "body2"}>
          <strong>{label}:</strong> {getScoreDisplay(score)}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, percent))}
          sx={{
            height: isLarge ? 8 : 6,
            borderRadius: 5,
            backgroundColor: '#e5e7eb', // gray-200 for the track
            '& .MuiLinearProgress-bar': {
              backgroundColor: getScoreColor(score),
            },
          }}
        />
      </Box>
    );
  };


  const SummaryContent = () => {
    if (!processedData) return null;
    return (
        <Card sx={{ mb: 4 }}>
          <Box sx={{ bgcolor: getScoreColor(processedData.fire_risk.score), color: "white", p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5">
                <strong>Historical Fire Risk: </strong> {getScoreDisplay(processedData.fire_risk.score)}
              </Typography>
              <RiskRatings rank={processedData.fire_risk.score + 3} />
            </Box>
          </Box>

          <CardContent>
            <Box>
              <Typography variant="h6" color="text.secondary">
                Rank:
              </Typography>
              <Typography variant="body1" gutterBottom>
                {riskSectionSentences.fire_rank[reportData.fire_risk.score]}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Summary:
              </Typography>
              <Typography variant="body1" gutterBottom>
                {riskSectionSentences.fire_summary[processedData.fire_risk.score]}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Details:
              </Typography>
              <Typography variant="body1">
                The historical fire risk rating is determined through a detailed comparative analysis of the selected location and Pacific Palisades, spanning a 46 year period. This analysis evaluates 10 fire risk metrics to assess if conditions were less than, equal to, or greater than those in Pacific Palisades over this time frame.
                <br/><br/>
                The metrics used in this analysis are:<br/>
                Burning Index (BI), Energy Release Component (ERC), Reference Evapotranspiration (ETR), 100 Hour Dead Fuel Moisture (FM100), 1000 Hour Dead Fuel Moisture (FM1000), Maximum Relative Humidity (RMAX), Minimum Relative Humidity (RMIN), Specific Humidity (SPH), Potential Evapotranspiration (PET), and Mean Vapor Pressure Deficit (VPD).
                <br/><br/>
                The grey dotted line seen on the charts represent the "unprecedented" conditions last recorded before the 2025 Palisades Fire.
              </Typography>
            </Box>
          </CardContent>


          <CardContent sx={{ bgcolor: "white" }}>
            <FireRankBar value={calculateFireRiskValue(processedData.fire_risk.ranks.all, processedData.fire_risk.score)} showLabels={true} height={10}/>
          </CardContent>
        </Card>
    );
  };


  return (
    <div className="bg-gray-100 min-h-screen p-8 font-sans antialiased text-gray-800">
      <div className="max-w-3xl mx-auto bg-white shadow-xl rounded-xl p-8">
        <Box sx={{ pt: 2 }}>
          {error && <Typography color="error">{error}</Typography>}
          {processedData ? <SummaryContent /> : <p>Loading report...</p>}
        </Box>
      </div>
    </div>
  );
}
